"use client";

import { rashis } from "@ishvaram/domain-data/rashis";
import Link from "next/link";

import type { ActiveTransit, SadeSatiInfo } from "@/lib/api/transits";
import { SATURN_STATUS_STYLES, type SadeSatiPhase } from "@/lib/saturn-status";

export function SaturnPositionCard({
  saturnTransit,
  calculationDate,
}: {
  saturnTransit: ActiveTransit | null;
  calculationDate: string;
}) {
  return (
    <div className="card-sacred p-6 mb-5">
      <p className="text-[12px] uppercase tracking-wider font-semibold mb-1 text-ish-accent">
        Engine-computed current position
      </p>
      {saturnTransit ? (
        <>
          <h2 className="font-serif text-2xl font-bold text-ish-dark">
            Shani in {saturnTransit.current_rashi_name_en} ({saturnTransit.current_rashi_name_hi})
          </h2>
          <p className="mt-1 text-[13px] text-ish-text-muted">
            {saturnTransit.rashi_degree.toFixed(1)}° · {saturnTransit.nakshatra} ·{" "}
            {saturnTransit.is_retrograde ? "Retrograde" : "Direct"}
          </p>
          <p className="mt-3 text-[12px] text-ish-text-faint">
            Calculated for {calculationDate} from the Vedic transit engine.
          </p>
        </>
      ) : (
        <p className="text-[14px] text-ish-text-muted">Calculating Shani&apos;s live position…</p>
      )}
    </div>
  );
}

export function RashiPicker({
  selectedRashi,
  onSelect,
  sadeSati,
  saturnTransit,
  isLoading,
}: {
  selectedRashi: string | null;
  onSelect: (id: string | null) => void;
  sadeSati: SadeSatiInfo | null | undefined;
  saturnTransit: ActiveTransit | null;
  isLoading: boolean;
}) {
  const selectedData = selectedRashi ? rashis.find((rashi) => rashi.id === selectedRashi) : null;
  const phase: SadeSatiPhase = sadeSati?.phase ?? "none";
  const colors = SATURN_STATUS_STYLES[phase];

  return (
    <div className="card-sacred p-5 mb-5">
      <p className="text-[14px] font-semibold mb-3 text-ish-dark">
        Check your Moon sign against Shani&apos;s live position
      </p>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
        {rashis.map((rashi) => {
          const isSelected = selectedRashi === rashi.id;
          return (
            <button
              key={rashi.id}
              type="button"
              onClick={() => onSelect(isSelected ? null : rashi.id)}
              className="flex flex-col items-center gap-0.5 rounded-lg border p-2 text-center transition-all"
              style={{
                backgroundColor: isSelected
                  ? "var(--ish-accent-light)"
                  : "var(--color-surface-warm)",
                borderColor: isSelected ? "var(--ish-accent)" : "var(--ish-border)",
              }}
            >
              <span className="text-[11px] font-bold leading-tight text-ish-dark">
                {rashi.name_en}
              </span>
              <span className="text-[10px] text-ish-text-faint">{rashi.name_hi}</span>
            </button>
          );
        })}
      </div>

      {selectedData && (
        <div
          className="mt-4 rounded-lg p-4"
          style={{ backgroundColor: colors.bg, border: `1px solid ${colors.border}` }}
        >
          {isLoading || sadeSati === undefined ? (
            <p className="text-[13px] text-ish-text-muted">Checking the live Saturn transit…</p>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between gap-3">
                <span className="text-[14px] font-bold" style={{ color: colors.text }}>
                  {selectedData.name_en} · {selectedData.name_hi}
                </span>
                <span
                  className="rounded px-2.5 py-1 text-[11px] font-bold"
                  style={{ backgroundColor: colors.badgeBg, color: colors.badge }}
                >
                  {sadeSati ? `${sadeSati.phase.toUpperCase()} PHASE` : "NOT ACTIVE"}
                </span>
              </div>
              <p className="text-[13px]" style={{ color: colors.text }}>
                {sadeSati
                  ? sadeSati.phase_description_en
                  : `Shani in ${saturnTransit?.current_rashi_name_en ?? "its current sign"} is not in the 12th, 1st, or 2nd house from this Moon sign.`}
              </p>
            </>
          )}
        </div>
      )}

      {!selectedRashi && (
        <p className="mt-3 text-center text-[13px] text-ish-text-faint">
          Don&apos;t know your Moon sign?{" "}
          <Link href="/rashi-calculator" className="font-semibold text-ish-accent">
            Find it free →
          </Link>
        </p>
      )}
    </div>
  );
}
